import { SxProps, Theme } from '@mui/material/styles';

import { columnNestedBlockWrapperSx } from 'src/helpers/heroLayoutUtils';
import { DividerSpacing } from 'src/helpers/dividerSpacing';
import { IBlockSpacing, IBlockSpacingSides, IPageBlock } from 'src/interfaces/PageBlock';

export const DEFAULT_DIVIDER_BLOCK_SPACING: IBlockSpacing = {
  margin: { top: 2, bottom: 2 },
};

function normalizeSideValue(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return n;
}

function normalizeBlockSpacingSides(
  sides?: IBlockSpacingSides,
): IBlockSpacingSides | undefined {
  if (!sides) return undefined;
  const top = normalizeSideValue(sides.top);
  const right = normalizeSideValue(sides.right);
  const bottom = normalizeSideValue(sides.bottom);
  const left = normalizeSideValue(sides.left);
  if (top == null && right == null && bottom == null && left == null) return undefined;
  return {
    ...(top != null ? { top } : {}),
    ...(right != null ? { right } : {}),
    ...(bottom != null ? { bottom } : {}),
    ...(left != null ? { left } : {}),
  };
}

export function normalizeBlockSpacing(spacing?: IBlockSpacing): IBlockSpacing | undefined {
  if (!spacing) return undefined;
  const margin = normalizeBlockSpacingSides(spacing.margin);
  const padding = normalizeBlockSpacingSides(spacing.padding);
  if (!margin && !padding) return undefined;
  return {
    ...(margin ? { margin } : {}),
    ...(padding ? { padding } : {}),
  };
}

export function hasBlockSpacing(spacing?: IBlockSpacing): boolean {
  return normalizeBlockSpacing(spacing) != null;
}

export function dividerSpacingToBlockSpacing(dividerSpacing: unknown): IBlockSpacing {
  const units: Record<DividerSpacing, number> = { small: 1, medium: 2, large: 4 };
  const key =
    dividerSpacing === 'small' || dividerSpacing === 'medium' || dividerSpacing === 'large'
      ? dividerSpacing
      : 'medium';
  const value = units[key];
  return { margin: { top: value, bottom: value } };
}

function sidesToSx(
  sides: IBlockSpacingSides | undefined,
  prefix: 'm' | 'p',
): SxProps<Theme> {
  if (!sides) return {};
  const map = {
    top: `${prefix}t`,
    right: `${prefix}r`,
    bottom: `${prefix}b`,
    left: `${prefix}l`,
  } as const;
  const sx: Record<string, number> = {};
  (Object.keys(map) as (keyof IBlockSpacingSides)[]).forEach(side => {
    const value = sides[side];
    if (value != null && value > 0) {
      sx[map[side]] = value;
    }
  });
  return sx;
}

export function blockSpacingSx(spacing?: IBlockSpacing): SxProps<Theme> {
  const normalized = normalizeBlockSpacing(spacing);
  if (!normalized) return {};
  return {
    ...sidesToSx(normalized.margin, 'm'),
    ...sidesToSx(normalized.padding, 'p'),
  } as SxProps<Theme>;
}

export function nestedPageBlockWrapperSx(block: IPageBlock): SxProps<Theme> {
  return {
    ...columnNestedBlockWrapperSx(block),
    ...blockSpacingSx(block.spacing),
  } as SxProps<Theme>;
}
